import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Image,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Field } from '@/components/ui/field';
import { toaster } from '@/components/ui/toaster';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLogin } from 'ra-core';
import { persistStorageService } from '@/features/auth';

// Function to parse URL hash parameters
const parseHashParams = (hash: string) => {
  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(hash);

  for (const [key, value] of searchParams.entries()) {
    params[key] = value;
  }

  return params;
};

// Function to handle invitation token from URL
const handleInvitationToken = () => {
  // clear current tokens
  persistStorageService.batchRemovePersistItems({
    names: ['token', 'refresh_token', 'user', 'expires_at'],
  });

  const hash = window.location.hash;

  if (!hash) return null;

  const params = parseHashParams(hash?.substring(1));

  console.log({ params });

  if (params.access_token) {
    const invitationData = {
      access_token: params.access_token,
      refresh_token: params.refresh_token || '',
      expires_at: params.expires_at
        ? parseInt(params.expires_at)
        : Date.now() + 3600000, // 1 hour default
    };

    // Store tokens in localStorage
    persistStorageService.setPersistItem({
      name: 'token',
      value: params.access_token,
    });
    persistStorageService.setPersistItem({
      name: 'expires_at',
      value: params.expires_at || (Date.now() + 3600000).toString(),
    });

    return invitationData;
  }

  return null;
};

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({ email: '', password: '' });
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Get the redirect path from location state or default to /
  const from = location.state?.from?.pathname || '/';

  // Handle invitation token on component mount
  useEffect(() => {
    const invitationData = handleInvitationToken();
    console.log('invitationData', invitationData);

    if (invitationData) {
      // Clean up URL hash
      // window.history.replaceState(
      //   {},
      //   document.title,
      //   window.location.pathname + window.location.search,
      // );

      // Redirect to complete signup page
      navigate('/complete-signup');
      return;
    }
  }, [navigate]);

  const validateForm = () => {
    let isValid = true;
    const newErrors = { email: '', password: '' };

    if (!email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }

    if (!password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setLoginError(null);

    try {
      // Use the login function from ra-core which will use your authProvider
      await login({ username: email, password });

      // On successful login, navigate to the original requested page or home
      navigate(from);

      toaster.create({
        title: 'Login successful',
        type: 'success',
        duration: 3000,
      });
    } catch (error) {
      console.error('Login error:', error);
      setLoginError(
        error instanceof Error
          ? error.message
          : 'Authentication failed. Please check your credentials.',
      );

      toaster.create({
        title: 'Login failed',
        description: loginError,
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} minH="100vh">
      {/* Form Section */}
      <Flex align="center" justify="center" bg="gray.50">
        <Box
          w="full"
          maxW="md"
          p={8}
          bg="white"
          boxShadow="lg"
          borderRadius="lg"
          height="80%"
        >
          <VStack gap={6} align="stretch">
            <Flex justify="center" width="100%" mb={5} mt={5}>
              {' '}
              {/* Add this Flex container */}
              <Image
                src="https://cdn.clubplanner.com/production-v2/current//images/icons/clubplanner_logo.svg"
                alt="LIMU Chatbots"
                width={180}
                height={50}
                objectFit="contain"
              />
            </Flex>
            <Heading size="lg" fontWeight="bold" textAlign="center">
              Log in to admin panel
            </Heading>
            <br />
            <form onSubmit={handleSubmit}>
              <VStack gap={4}>
                <Field
                  label="Email"
                  invalid={!!errors.email}
                  errorText={errors.email}
                >
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    size="lg"
                    borderRadius="md"
                  />
                </Field>
                <Field
                  label="Password"
                  invalid={!!errors.password}
                  errorText={errors.password}
                >
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    size="lg"
                    borderRadius="md"
                  />
                </Field>
                {loginError && (
                  <Text color="red.500" fontSize="sm">
                    {loginError}
                  </Text>
                )}
                <Button
                  type="submit"
                  w="full"
                  bg="orange.500"
                  size="lg"
                  fontSize="md"
                  fontWeight="bold"
                  mt={2}
                  loading={isLoading}
                  loadingText="Logging in..."
                  _hover={{ bg: 'gray.900', color: 'white' }}
                >
                  Login
                </Button>
              </VStack>
            </form>
          </VStack>
        </Box>
      </Flex>

      {/* Quote Section */}
      <Flex
        bg="orange.500"
        align="center"
        justify="center"
        display={{ base: 'none', md: 'flex' }}
      >
        <VStack maxW="md" p={12} color="gray.900" gap={8} align="center">
          <Text
            fontSize="2xl"
            fontWeight="bold"
            lineHeight="shorter"
            textAlign="center"
          >
            "Clubplanner is incredible! They've revolutionized my gym
            management, allowing me to focus on my members while reducing admin
            time."
          </Text>
          <VStack gap={3} align="center">
            <Text fontSize="lg" fontWeight="semibold">
              Sarah Johnson
            </Text>
            <Text fontSize="md" opacity={0.8}>
              Gym Owner, FitLife Studios
            </Text>
          </VStack>
        </VStack>
      </Flex>
    </Grid>
  );
}

export default LoginPage;
